import { DatePicker, Form, Input, InputNumber, Select, Space } from 'antd';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import type { ReactNode } from 'react';

import {
  updateCustomerCommercialTerms,
  updateCustomerConfidentiality,
  updateCustomerProfile,
} from '../../api/customers';
import type { CustomerDetail } from '../../api/customers';
import type { Currency } from '../../api/currencies';
import type { TaxRate } from '../../api/taxRates';
import { PermissionCodes } from '../../auth/permissions';
import { useAuth } from '../../auth/useAuth';
import { CustomerEditCard } from './CustomerEditCard';
import { groupOptions, industryOptions } from './customerDisplay';

/** Khuôn ngày dùng khi gửi lên máy chủ. Trùng với khuôn của kiểu ngày trong .NET. */
const isoDateFormat = 'YYYY-MM-DD';

/** Khuôn ngày hiển thị cho người dùng Việt Nam. */
const displayDateFormat = 'DD/MM/YYYY';

/** Giá trị của thẻ sửa thông tin giao hàng, liên lạc và phân loại. */
interface ProfileFormValues {
  readonly deliveryAddress: string | null;
  readonly deliveryNote: string | null;
  readonly phoneNumber: string | null;
  readonly email: string | null;
  readonly website: string | null;
  readonly industry: number | null;
  readonly group: number | null;
  readonly note: string | null;
}

/** Giá trị của thẻ sửa điều khoản thương mại. */
interface CommercialFormValues {
  readonly paymentTermDays: number | null;
  readonly creditLimitAmount: number | null;
  readonly defaultCurrencyCode: string | null;
  readonly defaultValueAddedTaxRateId: string | null;
  readonly defaultRetentionPercent: number | null;
}

/** Giá trị của thẻ sửa thông tin bảo mật. */
interface ConfidentialityFormValues {
  readonly nonDisclosureExpiresOn: dayjs.Dayjs | null;
  readonly confidentialityNote: string | null;
}

/** Tham số của phần thương mại. */
export interface CustomerCommerceSectionProps {
  /** Khách hàng đang xem. */
  readonly customer: CustomerDetail;

  /** Danh mục tiền tệ, đã nạp sẵn bởi trang chi tiết. */
  readonly currencies: readonly Currency[];

  /** Danh mục thuế suất đang có hiệu lực, đã nạp sẵn bởi trang chi tiết. */
  readonly taxRates: readonly TaxRate[];

  /** Nhận khách hàng sau khi sửa. */
  readonly onSaved: (updated: CustomerDetail) => void;
}

/**
 * Ba thẻ về mặt thương mại: thông tin làm việc, điều khoản, và bảo mật.
 *
 * Thẻ điều khoản thương mại có quyền riêng vì nội dung của nó là quyết định tài chính: nới hạn mức
 * công nợ cho một khách đang nợ quá hạn là việc phải có người chịu trách nhiệm. Hai thẻ còn lại dùng
 * quyền sửa thông tin chung — sai một số điện thoại thì hậu quả nhiều nhất là gọi nhầm.
 */
export function CustomerCommerceSection({
  customer,
  currencies,
  taxRates,
  onSaved,
}: CustomerCommerceSectionProps): ReactNode {
  const { hasPermission } = useAuth();

  const canUpdateProfile = hasPermission(PermissionCodes.CustomerUpdate);
  const canUpdateCommercialTerms = hasPermission(
    PermissionCodes.CustomerUpdateCommercialTerms,
  );

  /*
    Giữ cùng một đối tượng giá trị ban đầu qua các lần vẽ, chỉ dựng lại khi dữ liệu từ máy chủ đổi.
    Xem ghi chú cùng nội dung ở phần định danh: nếu tạo mới ở mỗi lần vẽ thì thẻ sửa sẽ ghi đè lên
    những gì người dùng đang gõ mỗi khi trang cha vẽ lại.
  */
  const profileValues = useMemo<ProfileFormValues>(
    () => ({
      deliveryAddress: customer.deliveryAddress,
      deliveryNote: customer.deliveryNote,
      phoneNumber: customer.phoneNumber,
      email: customer.email,
      website: customer.website,
      industry: customer.industry,
      group: customer.group,
      note: customer.note,
    }),
    [customer],
  );

  const commercialValues = useMemo<CommercialFormValues>(
    () => ({
      paymentTermDays: customer.paymentTermDays,
      creditLimitAmount: customer.creditLimitAmount,
      defaultCurrencyCode: customer.defaultCurrencyCode,
      defaultValueAddedTaxRateId: customer.defaultValueAddedTaxRateId,
      defaultRetentionPercent: customer.defaultRetentionPercent,
    }),
    [customer],
  );

  const confidentialityValues = useMemo<ConfidentialityFormValues>(
    () => ({
      nonDisclosureExpiresOn:
        customer.nonDisclosureExpiresOn === null
          ? null
          : dayjs(customer.nonDisclosureExpiresOn, isoDateFormat),
      confidentialityNote: customer.confidentialityNote,
    }),
    [customer],
  );

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <CustomerEditCard<ProfileFormValues>
        title="Giao hàng, liên lạc và phân loại"
        canEdit={canUpdateProfile}
        initialValues={profileValues}
        onSave={async (values) => await updateCustomerProfile(customer.id, values)}
        onSaved={onSaved}
      >
        <Form.Item
          name="deliveryAddress"
          label="Địa chỉ giao hàng"
          extra="Thường khai ở cấp nhà máy. Khác địa chỉ hóa đơn."
        >
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item
          name="deliveryNote"
          label="Hướng dẫn giao hàng"
          extra={
            'Vào cổng nào, giờ nào, gặp ai. Nghe như ghi chú phụ nhưng đây là thứ hay làm mất một '
            + 'buổi: xe tới cổng chính trong khi hàng phải vào cổng phụ.'
          }
        >
          <Input.TextArea rows={2} />
        </Form.Item>

        <Form.Item
          name="phoneNumber"
          label="Số điện thoại"
          extra="Số chung của tổ chức. Người liên hệ cụ thể khai riêng khi có bảng người liên hệ."
        >
          <Input />
        </Form.Item>

        <Form.Item name="email" label="Thư điện tử">
          <Input />
        </Form.Item>

        <Form.Item name="website" label="Trang thông tin">
          <Input placeholder="https://" />
        </Form.Item>

        <Form.Item
          name="industry"
          label="Ngành sản xuất"
          extra="Dùng để lọc và thống kê. Biết mình từng làm cho mấy khách cùng ngành là thông tin thật khi báo giá."
        >
          <Select<number | null> allowClear options={[...industryOptions]} />
        </Form.Item>

        <Form.Item name="group" label="Nhóm khách hàng">
          <Select<number | null> allowClear options={[...groupOptions]} />
        </Form.Item>

        <Form.Item name="note" label="Ghi chú">
          <Input.TextArea rows={3} />
        </Form.Item>
      </CustomerEditCard>

      <CustomerEditCard<CommercialFormValues>
        title="Điều khoản thương mại"
        description={
          'Đây là giá trị mặc định điền sẵn khi lập báo giá và hợp đồng, không phải ràng buộc. Từng '
          + 'hợp đồng vẫn thỏa thuận khác đi được — nhưng khi khác đi thì người lập phải sửa có ý '
          + 'thức, và đó là điểm khác giữa một điều khoản được thương lượng và một điều khoản bị bỏ '
          + 'trống vì quên.'
        }
        canEdit={canUpdateCommercialTerms}
        initialValues={commercialValues}
        onSave={async (values) => await updateCustomerCommercialTerms(customer.id, values)}
        onSaved={onSaved}
      >
        <Form.Item
          name="paymentTermDays"
          label="Số ngày thanh toán"
          extra="Từ 0 đến 365 ngày. Để trống nếu chưa thống nhất với khách."
        >
          <InputNumber min={0} max={365} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="creditLimitAmount"
          label="Hạn mức công nợ"
          extra={
            'Để trống nghĩa là KHÔNG đặt hạn mức. Số 0 nghĩa là không cho nợ đồng nào — hai điều đó '
            + 'ngược nhau, nên đừng dùng số 0 thay cho việc để trống.'
          }
        >
          {/*
            Ngăn cách nghìn bằng dấu chấm theo cách viết số của Việt Nam. Hạn mức công nợ thường ở
            mức hàng tỷ đồng, và một dãy mười chữ số không có dấu ngăn cách thì gần như không đọc
            được — đây là chỗ rất dễ gõ thừa hoặc thiếu một chữ số.
          */}
          <InputNumber<number>
            min={0}
            style={{ width: '100%' }}
            formatter={(value) =>
              value === undefined
                ? ''
                : value.toLocaleString('vi-VN', { useGrouping: true })
            }
            parser={(displayValue) => Number((displayValue ?? '').replace(/\./gu, ''))}
          />
        </Form.Item>

        <Form.Item name="defaultCurrencyCode" label="Đồng tiền mặc định">
          <Select<string | null>
            allowClear
            showSearch
            optionFilterProp="label"
            options={currencies.map((currency) => ({
              value: currency.code,
              label: `${currency.code} — ${currency.name}`,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="defaultValueAddedTaxRateId"
          label="Thuế suất giá trị gia tăng mặc định"
          extra="Chỉ hiện những thuế suất đang có hiệu lực hôm nay."
        >
          <Select<string | null>
            allowClear
            showSearch
            optionFilterProp="label"
            options={taxRates.map((taxRate) => ({
              value: taxRate.id,
              label: `${taxRate.code} — ${taxRate.name}`,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="defaultRetentionPercent"
          label="Tỷ lệ giữ lại đến hết bảo hành"
          extra="Tính theo phần trăm, từ 0 đến 100."
        >
          <InputNumber min={0} max={100} style={{ width: '100%' }} suffix="%" />
        </Form.Item>
      </CustomerEditCard>

      <CustomerEditCard<ConfidentialityFormValues>
        title="Bảo mật thông tin"
        description={
          customer.nonDisclosureExpiresWithinSixtyDays
            ? 'Thỏa thuận bảo mật sắp hết hiệu lực hoặc đã hết. Gửi tài liệu kỹ thuật cho khách khi '
              + 'thỏa thuận hết hiệu lực là rủi ro pháp lý.'
            : 'Ngày hết hiệu lực dùng để nhắc gia hạn trước sáu mươi ngày, nên không nhận ngày đã qua.'
        }
        canEdit={canUpdateProfile}
        initialValues={confidentialityValues}
        onSave={async (values) =>
          await updateCustomerConfidentiality(customer.id, {
            // Giữ nguyên liên kết tài liệu: nó được chọn ở module tài liệu, không phải ở đây.
            nonDisclosureDocumentId: customer.nonDisclosureDocumentId,
            nonDisclosureExpiresOn:
              values.nonDisclosureExpiresOn === null
                ? null
                : values.nonDisclosureExpiresOn.format(isoDateFormat),
            confidentialityNote: values.confidentialityNote,
          })
        }
        onSaved={onSaved}
      >
        <Form.Item name="nonDisclosureExpiresOn" label="Ngày hết hiệu lực thỏa thuận bảo mật">
          <DatePicker format={displayDateFormat} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="confidentialityNote"
          label="Lưu ý bảo mật riêng"
          extra={
            'Ví dụ thật trong ngành máy thị giác: "Không được chụp ảnh trong xưởng", '
            + '"Mọi tài liệu phải đóng dấu CONFIDENTIAL". Vi phạm những điều này làm mất hợp đồng.'
          }
        >
          <Input.TextArea rows={3} />
        </Form.Item>
      </CustomerEditCard>
    </Space>
  );
}
